import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, Shield, ArrowUpRight } from "lucide-react";

export default function Hero() {
  return (
    <section className="relative min-h-[100svh] flex items-center pt-28 pb-20 overflow-hidden root-grid">
      <div className="aurora" />
      {/* Background root network SVG */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.08]"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id="rg" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* Root lines */}
        {Array.from({ length: 20 }).map((_, i) => (
          <line
            key={i}
            x1={720}
            y1={450}
            x2={720 + Math.cos((i / 20) * Math.PI * 2) * 800}
            y2={450 + Math.sin((i / 20) * Math.PI * 2) * 800}
            stroke="#10B981"
            strokeWidth="0.5"
          />
        ))}
        {/* Nodes */}
        {Array.from({ length: 24 }).map((_, i) => {
          const angle = (i / 24) * Math.PI * 2;
          const r = 200 + (i % 4) * 130;
          const cx = 720 + Math.cos(angle) * r;
          const cy = 450 + Math.sin(angle) * r;
          return (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r="3"
              fill="#10B981"
            />
          );
        })}
      </svg>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/5 text-emerald-300 text-xs font-mono mb-8"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            CYBERSECURITY · ISO 27001 · FRANCE 🇫🇷 · SÉNÉGAL 🇸🇳 · REMOTE
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-[44px] leading-[1.02] sm:text-6xl lg:text-[80px] xl:text-[92px] font-bold tracking-tight"
          >
            Des racines{" "}
            <span className="relative inline-block">
              <span className="italic font-medium text-emerald-400">solides</span>
              <svg
                className="absolute -bottom-2 left-0 w-full"
                height="14"
                viewBox="0 0 200 14"
                fill="none"
              >
                <path
                  d="M2 8 Q 50 2, 100 7 T 198 6"
                  stroke="#EAB308"
                  strokeWidth="2"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>
            <br />
            pour une sécurité
            <br />
            sans faille.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 text-lg md:text-xl text-gray-400 max-w-2xl leading-relaxed"
          >
            BaobSec aide les organisations à structurer, renforcer et professionnaliser
            leur cybersécurité — de la stratégie ISO 27001 jusqu'aux opérations SOC.
            Une double expertise <span className="text-white">gouvernance</span> &{" "}
            <span className="text-white">défense</span>, portée par des experts certifiés.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#quiz"
              data-testid="hero-cta-button"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold transition-all hover:-translate-y-[2px] glow-emerald"
            >
              Tester ma maturité (60s)
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <a
              href="#services"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/10 hover:border-emerald-500/50 text-white hover:text-emerald-400 transition-colors"
            >
              <Shield className="w-4 h-4" />
              Découvrir nos services
            </a>
          </motion.div>
        </div>

        {/* Right side card with baobab metaphor */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-3xl overflow-hidden border border-white/5 bg-[#0C1410]">
            <img
              src="https://images.pexels.com/photos/30762641/pexels-photo-30762641.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900"
              alt="Baobab tree"
              className="w-full h-[480px] md:h-[560px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050A07] via-[#050A07]/40 to-transparent" />
            {/* Floating badges */}
            <div className="absolute top-6 left-6 right-6 flex justify-between gap-4">
              <div className="px-3 py-2 rounded-xl bg-black/50 backdrop-blur-md border border-white/10 text-xs font-mono text-emerald-400">
                /// ISO 27001
              </div>
              <div className="px-3 py-2 rounded-xl bg-black/50 backdrop-blur-md border border-white/10 text-xs font-mono text-emerald-400">
                EBIOS RM
              </div>
            </div>
            <div className="absolute bottom-6 left-6 right-6">
              <div className="rounded-2xl border border-white/10 bg-black/55 backdrop-blur-md p-5">
                <div className="font-mono text-[11px] text-emerald-400 mb-2 tracking-widest">
                  // PHILOSOPHIE
                </div>
                <div className="font-display text-xl md:text-2xl leading-snug">
                  La cybersécurité aux{" "}
                  <span className="italic text-emerald-400">racines profondes.</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <a
        href="#stats"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-500 hover:text-emerald-400 transition-colors flex flex-col items-center gap-2 font-mono text-[11px] tracking-widest"
        data-testid="hero-scroll-indicator"
      >
        SCROLL
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </a>
    </section>
  );
}
